import { useState, useEffect } from 'react';
import { Routes, Route, useParams, useNavigate } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { MainLayout } from './components/layout/MainLayout';
import BibleReader from './components/BibleReader';
import { AuthModal } from './components/auth/AuthModal';
import { ProtectedRoute } from './components/auth/ProtectedRoute';
import { SearchPage } from './pages/SearchPage';
import { NotesPage } from './pages/NotesPage';
import { UserPreferences } from './pages/UserPreferences';
import MoodScriptures from './pages/MoodScriptures';
import { StudyGroupsView } from './components/study/StudyGroupsView';
import { StudyGroupDetail } from './components/study/StudyGroupDetail';
import { BibleStudyView } from './components/study/BibleStudyView';
import { useAuthStore } from './services/auth';
import { db } from './lib/db';
import { useBibleStore } from './store/bibleStore';
import { useFavoritesStore } from './stores/favoritesStore';
import { useBookmarkStore } from './stores/bookmarkStore';
import AIInsightsPage from './pages/AIInsightsPage';
import { ApiProvider } from './context/ApiContext';
import { useStore } from './store';
import AuthCallback from './pages/AuthCallback';
import { AuthProvider } from './contexts/AuthContext';
import { Tutorial } from './components/Tutorial';
import { useTutorialStore } from './stores/tutorialStore';
import { useSubscriptionStore } from './stores/subscriptionStore';
import PrivacyPolicy from './pages/PrivacyPolicy';
import EULA from './pages/EULA';

const EULAPage = EULA;

export default function App() {
  const [sidebarOpen, setSidebarOpen] = useState(window.innerWidth >= 768);
  const user = useAuthStore((state) => state.user);
  const showAuthModal = useAuthStore((state) => state.showAuthModal);
  const setShowAuthModal = useAuthStore((state) => state.setShowAuthModal);
  const selectedVersion = useBibleStore((state) => state.panels[0]?.bibleId || 'kjv');
  const loadFavorites = useFavoritesStore((state) => state.loadFavorites);
  const { setIsActive: setTutorialActive } = useTutorialStore();
  const loadSubscription = useSubscriptionStore((state) => state.loadSubscription);
  
  // Get persisted state
  const storedBook = useStore(state => state.selectedBook);
  const storedChapter = useStore(state => state.selectedChapter);
  const isLoading = useStore(state => state.isLoading);
  const loadBookmark = useStore(state => state.loadBookmark);
  const setStoredBook = useStore(state => state.setSelectedBook);
  const setStoredChapter = useStore(state => state.setSelectedChapter);

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 768) {
        setSidebarOpen(false);
      }
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Load bookmark when user logs in
  useEffect(() => {
    if (user) {
      Promise.all([
        loadBookmark(),
        loadFavorites(),
        loadSubscription()
      ]).catch(console.error);
    }
  }, [user, loadBookmark, loadFavorites, loadSubscription]);

  if (isLoading) {
    return <div className="flex items-center justify-center min-h-screen">
      <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-gold-400"></div>
    </div>;
  }

  return (
    <ApiProvider endpoint={import.meta.env.VITE_API_ENDPOINT || 'http://localhost:3000/api'}>
      <MainLayout sidebarOpen={sidebarOpen} setSidebarOpen={setSidebarOpen}>
        <Routes>
          <Route path="/" element={
            <BibleReader
              book={storedBook}
              chapter={storedChapter}
              version={selectedVersion}
              onBookChange={setStoredBook}
              onChapterChange={setStoredChapter}
            />
          } />
          <Route path="/read/:version/:book/:chapter" element={<BibleReaderWrapper />} />
          <Route path="/study" element={
            <BibleStudyView 
              reference={`${storedBook} ${storedChapter}`}
              onReferenceChange={(ref) => {
                const [book, chapter] = ref.split(' ');
                setStoredBook(book);
                setStoredChapter(parseInt(chapter));
              }}
            />
          } />
          <Route path="/search" element={
            <SearchPage />
          } />
          <Route path="/notes" element={
            <ProtectedRoute>
              <NotesPage />
            </ProtectedRoute>
          } />
          <Route path="/preferences" element={
            <ProtectedRoute>
              <UserPreferences />
            </ProtectedRoute>
          } />
          <Route path="/mood-scriptures" element={
            <ProtectedRoute>
              <MoodScriptures />
            </ProtectedRoute>
          } />
          <Route path="/ai-insights" element={
            <ProtectedRoute>
              <AIInsightsPage />
            </ProtectedRoute>
          } />
          <Route path="/study-groups" element={
            <StudyGroupsView />
          } />
          <Route path="/study-groups/:groupId" element={
            <StudyGroupDetail />
          } />
          <Route path="/auth/callback" element={<AuthCallback />} />
          <Route path="/eula" element={<EULAPage />} />
          <Route path="/privacy-policy" element={<PrivacyPolicy />} />
        </Routes>
        <AuthModal isOpen={showAuthModal} onClose={() => setShowAuthModal(false)} />
        <Tutorial />
        <ToastContainer position="bottom-right" theme="dark" />
        {process.env.NODE_ENV === 'development' && <ReactQueryDevtools />}
      </MainLayout>
    </ApiProvider>
  );
}

function BibleReaderWrapper() {
  const { version, book, chapter } = useParams();
  const navigate = useNavigate();
  const setStoredBook = useStore(state => state.setSelectedBook);
  const setStoredChapter = useStore(state => state.setSelectedChapter);

  useEffect(() => {
    if (book && chapter) {
      setStoredBook(book);
      setStoredChapter(parseInt(chapter));
    }
  }, [book, chapter, setStoredBook, setStoredChapter]);

  return (
    <BibleReader
      book={book || 'Genesis'}
      chapter={parseInt(chapter || '1')}
      version={version || 'kjv'}
      onBookChange={(newBook) => {
        navigate(`/read/${version}/${newBook}/${chapter}`);
      }}
      onChapterChange={(newChapter) => {
        navigate(`/read/${version}/${book}/${newChapter}`);
      }}
    />
  );
}